import React, { useState } from 'react';
import { 
  Receipt as ReceiptIcon, 
  QrCode, 
  Copy, 
  Check, 
  DollarSign, 
  Printer, 
  ArrowLeft, 
  Share2, 
  ShieldCheck, 
  Eye, 
  Download, 
  ExternalLink, 
  Loader2,
  ListOrdered,
  Search,
  Trash2,
  Calendar,
  User,
  CheckCircle2,
  Plus
} from 'lucide-react';
import { Receipt, ReceiptType, PaymentMethod } from '../types';
import { UserProfile } from '../../../types';
import { generatePixPayload } from '../utils/pixUtils';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { 
  openReceiptPdfInNewTab, 
  downloadReceiptPdf, 
  shareOrDownloadReceiptPdf,
  getReceiptTypeLabel
} from '../utils/receiptPdfUtils';
import ReceiptPdfPreviewModal from './ReceiptPdfPreviewModal';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';

interface ReceiptManagerProps {
  receipts?: Receipt[];
  initialReceipt?: Partial<Receipt>;
  user?: Partial<UserProfile>;
  providerName?: string;
  providerDocument?: string;
  providerPixKey?: string;
  providerCity?: string;
  onSaveReceipt?: (receipt: Receipt) => Promise<void> | void;
  onDeleteReceipt?: (receiptId: string) => Promise<void> | void;
  onBack: () => void;
  onExportPDF?: (receipt: Receipt) => void;
}

export default function ReceiptManager({
  receipts = [],
  initialReceipt,
  user,
  providerName = 'Prestador de Serviços',
  providerDocument,
  providerPixKey,
  providerCity = 'São Paulo',
  onSaveReceipt,
  onDeleteReceipt,
  onBack,
  onExportPDF
}: ReceiptManagerProps) {
  const effectiveBusinessName = user?.businessName || user?.ownerName || providerName;
  const effectiveDocument = user?.document || providerDocument || '';
  const effectivePixKey = user?.pixKey || providerPixKey || '';
  const effectiveCity = user?.city || providerCity;
  const effectiveState = user?.state || 'SP';
  const logoSrc = user?.logoUrl || user?.logoBase64;

  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
  const [historySearch, setHistorySearch] = useState('');
  const [currentReceiptId, setCurrentReceiptId] = useState(initialReceipt?.id || '');
  const [receiptNumber, setReceiptNumber] = useState(initialReceipt?.receiptNumber || `REC-${Date.now().toString().slice(-4)}`);
  const [receiptType, setReceiptType] = useState<ReceiptType>(initialReceipt?.receiptType || 'full');
  const [totalServiceValue, setTotalServiceValue] = useState<number>(initialReceipt?.totalServiceValue ?? 0);
  const [amountPaid, setAmountPaid] = useState<number>(initialReceipt?.amountPaid ?? 0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(initialReceipt?.paymentMethod || 'pix');
  const [clientName, setClientName] = useState(initialReceipt?.clientName || '');
  const [clientDocument, setClientDocument] = useState(initialReceipt?.clientDocument || '');
  const [description, setDescription] = useState(initialReceipt?.description || '');
  const [pixKey, setPixKey] = useState(effectivePixKey);
  const [copiedPix, setCopiedPix] = useState(false);
  const [installmentNumber, setInstallmentNumber] = useState(initialReceipt?.installmentNumber || 1);
  const [totalInstallments, setTotalInstallments] = useState(initialReceipt?.totalInstallments || 1);

  // Estados de Garantia Nativa
  const [hasWarranty, setHasWarranty] = useState(true);
  const [warrantyPeriod, setWarrantyPeriod] = useState<number>(initialReceipt?.warrantyPeriod || 90);
  const [warrantyUnit, setWarrantyUnit] = useState<'dias' | 'meses' | 'anos'>(initialReceipt?.warrantyUnit || 'dias');
  const [warrantyTerms, setWarrantyTerms] = useState<string>(initialReceipt?.warrantyTerms || 'Garantia de mão de obra e materiais conforme combinado.');

  // Estados do Modal e Exportação
  const [previewReceiptTarget, setPreviewReceiptTarget] = useState<Receipt | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');

  // Máscaras e Utilitários
  const maskDocument = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
      if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    }
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  const generateUuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleTypeChange = (type: ReceiptType) => {
    setReceiptType(type);
    if (type === 'full') {
      setAmountPaid(totalServiceValue);
    } else if (type === 'deposit') {
      setAmountPaid(Math.round(totalServiceValue * 0.5 * 100) / 100);
    } else if (type === 'final_balance') {
      setAmountPaid(Math.round(totalServiceValue * 0.5 * 100) / 100);
    }
  };

  const remainingBalance = Math.max(0, totalServiceValue - amountPaid);

  const pixPayload = pixKey ? generatePixPayload({
    pixKey,
    merchantName: effectiveBusinessName || 'PRESTADOR RMAX',
    merchantCity: effectiveCity,
    amount: amountPaid,
    txId: receiptNumber.replace(/[^a-zA-Z0-9]/g, ''),
    description: description.substring(0, 30)
  }) : '';

  const buildCurrentReceipt = (): Receipt => ({
    id: currentReceiptId || initialReceipt?.id || generateUuid(),
    userId: initialReceipt?.userId || user?.id || 'current_user',
    receiptNumber,
    totalServiceValue,
    amountPaid,
    remainingBalance,
    paymentMethod,
    receiptType,
    installmentNumber,
    totalInstallments,
    clientName,
    clientDocument,
    description,
    pixCopiaCola: pixPayload,
    warrantyPeriod: hasWarranty ? warrantyPeriod : undefined,
    warrantyUnit: hasWarranty ? warrantyUnit : undefined,
    warrantyTerms: hasWarranty ? warrantyTerms : undefined,
    issuedAt: initialReceipt?.issuedAt || new Date().toISOString()
  });

  const effectiveUserProfile: Partial<UserProfile> = {
    ...user,
    businessName: effectiveBusinessName,
    document: effectiveDocument,
    pixKey: pixKey || effectivePixKey,
    city: effectiveCity,
    state: effectiveState,
    phone: user?.phone,
    businessAddress: user?.businessAddress,
    businessNumber: user?.businessNumber,
    showAddress: user?.showAddress ?? true,
    logoUrl: user?.logoUrl,
    logoBase64: user?.logoBase64
  };

  const handleCopyPix = () => {
    if (!pixPayload) return;
    navigator.clipboard.writeText(pixPayload);
    setCopiedPix(true);
    setTimeout(() => setCopiedPix(false), 3000);
  };

  const handleAutoSave = async (): Promise<Receipt> => {
    const rec = buildCurrentReceipt();
    if (onSaveReceipt) {
      try {
        await onSaveReceipt(rec);
      } catch (err) {
        console.warn('Erro ao salvar recibo:', err);
      }
    }
    return rec;
  };

  const handleOpenPreview = async () => {
    const rec = await handleAutoSave();
    setPreviewReceiptTarget(rec);
    setIsPreviewOpen(true);
  };

  const handleOpenPreviewForReceipt = (rec: Receipt) => {
    setPreviewReceiptTarget(rec);
    setIsPreviewOpen(true);
  };

  const handleOpenInNewTab = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerationStep('Abrindo PDF...');
    try {
      const rec = await handleAutoSave();
      if (onExportPDF) onExportPDF(rec);
      await openReceiptPdfInNewTab(rec, effectiveUserProfile);
    } catch (err) {
      console.error('Falha ao abrir PDF do recibo:', err);
      alert('Erro ao abrir o PDF em nova aba.');
    } finally {
      setIsGenerating(false);
      setGenerationStep('');
    }
  };

  const handleOpenInNewTabForReceipt = async (rec: Receipt) => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerationStep('Abrindo PDF...');
    try {
      if (onExportPDF) onExportPDF(rec);
      await openReceiptPdfInNewTab(rec, effectiveUserProfile);
    } catch (err) {
      console.error('Falha ao abrir PDF do recibo:', err);
      alert('Erro ao abrir o PDF.');
    } finally {
      setIsGenerating(false);
      setGenerationStep('');
    }
  };

  const handleDownloadPDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerationStep('Gerando PDF...');
    try {
      const rec = previewReceiptTarget || await handleAutoSave();
      if (onExportPDF) onExportPDF(rec);
      await downloadReceiptPdf(rec, effectiveUserProfile);
    } catch (err) {
      console.error('Falha ao baixar PDF do recibo:', err);
    } finally {
      setIsGenerating(false);
      setGenerationStep('');
    }
  };

  const handleShareWhatsApp = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerationStep('Preparando envio...');
    try {
      const rec = await handleAutoSave();
      await shareOrDownloadReceiptPdf(rec, effectiveUserProfile);
    } catch (err) {
      console.error('Falha ao compartilhar recibo:', err);
    } finally {
      setIsGenerating(false);
      setGenerationStep('');
    }
  };

  const handleShareWhatsAppForReceipt = async (rec: Receipt) => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerationStep('Preparando envio...');
    try {
      await shareOrDownloadReceiptPdf(rec, effectiveUserProfile);
    } catch (err) {
      console.error('Falha ao compartilhar recibo:', err);
    } finally {
      setIsGenerating(false);
      setGenerationStep('');
    }
  };

  const handleSelectForEdit = (rec: Receipt) => {
    setCurrentReceiptId(rec.id);
    setReceiptNumber(rec.receiptNumber);
    setReceiptType(rec.receiptType || 'full');
    setTotalServiceValue(rec.totalServiceValue || rec.amountPaid);
    setAmountPaid(rec.amountPaid);
    setPaymentMethod(rec.paymentMethod || 'pix');
    setClientName(rec.clientName || '');
    setClientDocument(rec.clientDocument || '');
    setDescription(rec.description || '');
    setInstallmentNumber(rec.installmentNumber || 1);
    setTotalInstallments(rec.totalInstallments || 1);
    if (rec.warrantyPeriod) {
      setHasWarranty(true);
      setWarrantyPeriod(rec.warrantyPeriod);
      setWarrantyUnit(rec.warrantyUnit || 'dias');
      setWarrantyTerms(rec.warrantyTerms || 'Garantia de mão de obra e materiais conforme combinado.');
    } else {
      setHasWarranty(false);
    }
    setActiveTab('create');
  };

  const handleResetForm = () => {
    setCurrentReceiptId('');
    setReceiptNumber(`REC-${Date.now().toString().slice(-4)}`);
    setReceiptType('full');
    setTotalServiceValue(0);
    setAmountPaid(0);
    setPaymentMethod('pix');
    setClientName('');
    setClientDocument('');
    setDescription('');
    setHasWarranty(true);
    setWarrantyPeriod(90);
    setWarrantyUnit('dias');
    setWarrantyTerms('Garantia de mão de obra e materiais conforme combinado.');
    setActiveTab('create');
  };

  const handleDeleteReceiptAction = async (rec: Receipt) => {
    if (!onDeleteReceipt) return;
    const confirmDelete = window.confirm(`Tem certeza que deseja excluir o recibo Nº ${rec.receiptNumber} (${rec.clientName})?\nEsta ação não poderá ser desfeita.`);
    if (!confirmDelete) return;

    try {
      await onDeleteReceipt(rec.id);
      if (rec.id === currentReceiptId) {
        handleResetForm();
      }
    } catch (err) {
      console.error('Erro ao excluir recibo:', err);
      alert('Erro ao tentar excluir recibo.');
    }
  };

  const filteredReceipts = receipts.filter(r => {
    const term = historySearch.toLowerCase().trim();
    if (!term) return true;
    return (
      r.receiptNumber.toLowerCase().includes(term) ||
      (r.clientName && r.clientName.toLowerCase().includes(term)) ||
      (r.clientDocument && r.clientDocument.includes(term)) ||
      (r.description && r.description.toLowerCase().includes(term))
    );
  });

  const totalReceiptsAmount = receipts.reduce((acc, r) => acc + (Number(r.amountPaid) || 0), 0);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">
      {/* Header Sticky */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-100 px-4 sm:px-6 py-3.5 flex items-center shadow-xs w-full">
        <div className="max-w-5xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl transition-all cursor-pointer active:scale-95"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-brand uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                  {activeTab === 'create' ? receiptNumber : `${receipts.length} Recibos`}
                </span>
                <span className="text-xs text-zinc-500 font-bold hidden sm:inline">
                  Módulo de Recibos
                </span>
              </div>
              <h1 className="text-sm sm:text-base font-black text-zinc-900">
                {activeTab === 'create' ? (currentReceiptId ? 'Editar Recibo Financeiro' : 'Emissão de Recibo Financeiro & PIX') : 'Histórico de Recibos Emitidos'}
              </h1>
            </div>
          </div>

          {/* Ações Rápidas no Header */}
          <div className="flex items-center gap-2">
            <ScreenTourButton screenKey="receipts" />
            {activeTab === 'create' ? (
              <>
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                  title="Ver Prévia do Recibo"
                >
                  <Eye className="h-4 w-4 text-emerald-400" />
                  <span className="hidden sm:inline">Ver Prévia</span>
                </button>

                <button
                  type="button"
                  onClick={handleOpenInNewTab}
                  disabled={isGenerating}
                  className="px-3.5 py-2 bg-[#004B87] hover:bg-[#003B6B] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95 disabled:opacity-50"
                  title="Abrir PDF em Nova Aba"
                >
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                  <span className="hidden sm:inline">Abrir PDF</span>
                </button>

                <button
                  type="button"
                  onClick={handleShareWhatsApp}
                  disabled={isGenerating}
                  className="px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95 disabled:opacity-50"
                  title="Enviar no WhatsApp"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden md:inline">WhatsApp</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleResetForm}
                className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
              >
                <Plus className="h-4 w-4" />
                <span>Novo Recibo</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Abas de Navegação */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4">
        <div className="flex bg-zinc-200/70 p-1 rounded-2xl gap-1 text-xs font-bold">
          <button
            type="button"
            onClick={() => setActiveTab('create')}
            className={`flex-1 py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'create'
                ? 'bg-white text-zinc-900 shadow-xs font-black'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            <ReceiptIcon className="h-4 w-4" />
            <span>{currentReceiptId ? 'Editando Recibo' : 'Emissão de Recibo'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'history'
                ? 'bg-white text-zinc-900 shadow-xs font-black'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            <ListOrdered className="h-4 w-4" />
            <span>Recibos Emitidos ({receipts.length})</span>
          </button>
        </div>
      </div>

      {/* ─── ABA 1: CRIAÇÃO / EDIÇÃO DO RECIBO ─── */}
      {activeTab === 'create' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-6">
          
          {/* Banner de Destaque da Empresa (100% Tema Claro) */}
          <div className="bg-white rounded-3xl p-5 sm:p-6 text-zinc-900 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4 border border-zinc-200/80">
            <div className="flex items-center gap-4">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Logo da Empresa"
                  className="w-14 h-14 rounded-full object-cover bg-white border-2 border-zinc-200 p-0.5 shadow-xs shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-blue-50 border-2 border-blue-200 flex items-center justify-center font-black text-blue-700 text-lg shrink-0">
                  R MAX
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md border border-emerald-200">
                    Pro • Com sua Marca
                  </span>
                  {currentReceiptId && (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-800 px-2 py-0.5 rounded-md border border-amber-200">
                      Modo Edição
                    </span>
                  )}
                </div>
                <h2 className="text-base sm:text-lg font-black mt-0.5 text-zinc-950">{effectiveBusinessName}</h2>
                <p className="text-xs text-zinc-500 font-medium">
                  {effectiveDocument ? `${effectiveDocument} • ` : ''}{effectiveCity} - {effectiveState}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {currentReceiptId && (
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="px-3.5 py-2 bg-zinc-100 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-200 font-bold text-xs rounded-xl transition-all cursor-pointer active:scale-95"
                >
                  Criar Novo
                </button>
              )}
              <button
                type="button"
                onClick={handleOpenPreview}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
              >
                <Eye className="h-4 w-4" />
                <span>Ver Prévia</span>
              </button>
            </div>
          </div>

          {/* 1. Modalidade do Recibo */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-zinc-100 pb-3">
              <ReceiptIcon className="h-5 w-5 text-brand" />
              <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Tipo de Quitação</h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { type: 'full' as ReceiptType, label: 'Quitação Total', desc: '100% do valor pago' },
                { type: 'deposit' as ReceiptType, label: 'Sinal de Reserva', desc: '50% entrada do pedido' },
                { type: 'final_balance' as ReceiptType, label: 'Saldo Final', desc: '50% restante na entrega' },
                { type: 'installment' as ReceiptType, label: 'Parcelado', desc: 'Controle de parcelas' }
              ].map(item => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => handleTypeChange(item.type)}
                  className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer active:scale-95 ${
                    receiptType === item.type
                      ? 'bg-brand text-white border-brand shadow-xs'
                      : 'bg-zinc-50 border-zinc-100 hover:border-zinc-200'
                  }`}
                >
                  <div className={`text-xs font-black ${receiptType === item.type ? 'text-white' : 'text-zinc-900'}`}>
                    {item.label}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${receiptType === item.type ? 'text-white/80' : 'text-zinc-500'}`}>
                    {item.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 2. Valores e Pagador */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-5 shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-zinc-100 pb-3">
              <DollarSign className="h-5 w-5 text-brand" />
              <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Dados do Pagamento</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Nome do Cliente / Pagador *</label>
                <input
                  type="text"
                  placeholder="Ex: João da Silva"
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">CPF / CNPJ do Cliente (Opcional)</label>
                <input
                  type="text"
                  placeholder="000.000.000-00 ou 00.000.000/0001-00"
                  value={clientDocument}
                  onChange={e => setClientDocument(maskDocument(e.target.value))}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium font-mono placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Valor Total do Serviço (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={totalServiceValue || ''}
                  onChange={e => {
                    const val = parseFloat(e.target.value) || 0;
                    setTotalServiceValue(val);
                    if (receiptType === 'full') setAmountPaid(val);
                    else if (receiptType === 'deposit' || receiptType === 'final_balance') setAmountPaid(Math.round(val * 0.5 * 100) / 100);
                  }}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Valor Pago Neste Recibo (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={amountPaid || ''}
                  onChange={e => setAmountPaid(parseFloat(e.target.value) || 0)}
                  className="w-full bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-2.5 text-xs text-emerald-950 font-black focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Forma de Liquidação</label>
                <select
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value as PaymentMethod)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  <option value="pix">PIX Dinâmico (QR Code)</option>
                  <option value="dinheiro">Dinheiro em Espécie</option>
                  <option value="cartao_credito">Cartão de Crédito</option>
                  <option value="cartao_debito">Cartão de Débito</option>
                  <option value="transferencia">Transferência Bancária / TED</option>
                  <option value="boleto">Boleto Bancário</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Descrição do Serviço Prestado</label>
              <input
                type="text"
                placeholder="Ex: Instalação e serviços profissionais executados conforme pedido"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>
          </div>

          {/* 3. Termo de Garantia Automático */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-5 w-5 text-brand" />
                <div>
                  <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Termo de Garantia</h2>
                  <p className="text-[11px] text-zinc-500">Adicione prazo e termos de garantia impressos no recibo</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasWarranty}
                  onChange={e => setHasWarranty(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand"></div>
              </label>
            </div>

            {hasWarranty && (
              <div className="grid sm:grid-cols-4 gap-3 pt-2">
                <div className="space-y-1 col-span-1">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Prazo</label>
                  <input
                    type="number"
                    value={warrantyPeriod}
                    onChange={e => setWarrantyPeriod(parseInt(e.target.value) || 0)}
                    className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>

                <div className="space-y-1 col-span-1">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Unidade</label>
                  <select
                    value={warrantyUnit}
                    onChange={e => setWarrantyUnit(e.target.value as 'dias' | 'meses' | 'anos')}
                    className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    <option value="dias">Dias</option>
                    <option value="meses">Meses</option>
                    <option value="anos">Anos</option>
                  </select>
                </div>

                <div className="space-y-1 col-span-2">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Termos / Cobertura</label>
                  <input
                    type="text"
                    value={warrantyTerms}
                    onChange={e => setWarrantyTerms(e.target.value)}
                    placeholder="Ex: Sobre mão de obra e materiais conforme combinado"
                    className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 4. Módulo PIX Dinâmico */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2.5">
                <QrCode className="h-5 w-5 text-brand" />
                <div>
                  <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Chave PIX & Copia e Cola Oficial</h2>
                  <p className="text-[11px] text-zinc-500">Padrão BR Code homologado pelo Banco Central</p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-center">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Chave PIX do Emissor</label>
                <input
                  type="text"
                  placeholder="Insira sua chave PIX..."
                  value={pixKey}
                  onChange={e => setPixKey(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              {pixPayload && (
                <button
                  type="button"
                  onClick={handleCopyPix}
                  className="py-2.5 px-4 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm shadow-brand/20 cursor-pointer active:scale-95"
                >
                  {copiedPix ? <Check className="h-4 w-4 stroke-3" /> : <Copy className="h-4 w-4" />}
                  <span>{copiedPix ? 'Copiado!' : 'Copiar PIX'}</span>
                </button>
              )}
            </div>

            {pixPayload && (
              <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-100 text-[10px] text-zinc-600 font-mono break-all line-clamp-2">
                {pixPayload}
              </div>
            )}
          </div>

          {/* 5. Seção de Exportação */}
          <div className="mt-8 border-t border-zinc-200 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleOpenPreview}
                disabled={isGenerating}
                className="w-full font-bold py-3.5 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs sm:text-sm transition-all border shadow-sm active:scale-[0.98] cursor-pointer bg-zinc-900 text-white hover:bg-zinc-800 border-zinc-800 disabled:opacity-50"
              >
                <Eye className="h-4 w-4 text-emerald-400" />
                <span>Ver Prévia do Recibo</span>
              </button>

              <button
                type="button"
                onClick={handleOpenInNewTab}
                disabled={isGenerating}
                className="w-full font-black py-3.5 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs sm:text-sm transition-all shadow-md active:scale-[0.98] cursor-pointer uppercase tracking-wider bg-[#004B87] text-[#FFFFFF] hover:bg-[#003B6B] disabled:opacity-50"
              >
                {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                <span>{isGenerating ? (generationStep || 'Abrindo PDF...') : 'Abrir PDF em Nova Aba'}</span>
              </button>
            </div>
          </div>

        </div>
      )}

      {/* ─── ABA 2: HISTÓRICO DE RECIBOS EMITIDOS ─── */}
      {activeTab === 'history' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-4">
          {/* Métricas de Recibos */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Total Quitado Emitido</span>
              <span className="text-base font-black text-brand">{formatCurrencyBRL(totalReceiptsAmount)}</span>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Recibos Emitidos</span>
              <span className="text-base font-black text-zinc-900">{receipts.length} recibos</span>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm col-span-2 sm:col-span-1">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Ticket Médio Quitado</span>
              <span className="text-base font-black text-zinc-700">
                {formatCurrencyBRL(receipts.length > 0 ? totalReceiptsAmount / receipts.length : 0)}
              </span>
            </div>
          </div>

          {/* Campo de Busca */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar por número do recibo, cliente ou descrição..."
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              className="w-full bg-white border border-zinc-100 rounded-2xl pl-10 pr-4 py-3 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 shadow-xs"
            />
          </div>

          {/* Lista de Cards de Recibo */}
          {filteredReceipts.length === 0 ? (
            <div className="py-12 bg-white border border-dashed border-zinc-200 rounded-3xl text-center p-6 flex flex-col items-center justify-center">
              <ReceiptIcon className="h-10 w-10 text-zinc-300 mb-2" />
              <p className="text-xs font-bold text-zinc-700">Nenhum recibo encontrado.</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">Emita seu primeiro recibo com QR Code PIX clicando em "Novo Recibo".</p>
              <button
                type="button"
                onClick={handleResetForm}
                className="mt-3 px-4 py-2 bg-brand text-white text-xs font-bold rounded-xl active:scale-95 transition-all cursor-pointer"
              >
                Criar Primeiro Recibo
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReceipts.map(rec => {
                const typeInfo = getReceiptTypeLabel(rec.receiptType || 'full');
                const dateStr = new Date(rec.issuedAt || Date.now()).toLocaleDateString('pt-BR');

                return (
                  <div
                    key={rec.id}
                    className="bg-white border border-zinc-100 hover:border-zinc-200 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                  >
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-black text-xs bg-zinc-100 text-zinc-800 px-2 py-0.5 rounded-md border border-zinc-200">
                          {rec.receiptNumber}
                        </span>
                        <span className="text-[10px] font-black uppercase bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full">
                          {typeInfo.badge}
                        </span>
                        <span className="text-[11px] text-zinc-400 font-medium flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {dateStr}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-brand shrink-0" />
                        <h3 className="text-sm font-bold text-zinc-900 truncate">
                          {rec.clientName}
                          {rec.clientDocument && <span className="text-xs text-zinc-400 font-normal ml-1 font-mono">({rec.clientDocument})</span>}
                        </h3>
                      </div>

                      <p className="text-xs text-zinc-500 line-clamp-1">
                        {rec.description}
                      </p>

                      <div className="flex items-center gap-3 text-xs pt-1">
                        <span className="font-black text-emerald-700 text-sm">
                          {formatCurrencyBRL(rec.amountPaid)}
                        </span>
                        {rec.remainingBalance && rec.remainingBalance > 0 ? (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                            Saldo: {formatCurrencyBRL(rec.remainingBalance)}
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-0.5">
                            <CheckCircle2 className="h-3 w-3" /> 100% Quitado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Botões de Ação do Card */}
                    <div className="flex items-center gap-1.5 shrink-0 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-2 sm:pt-0 border-zinc-100">
                      <button
                        type="button"
                        onClick={() => handleOpenPreviewForReceipt(rec)}
                        className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl transition-all cursor-pointer active:scale-95"
                        title="Ver Prévia"
                      >
                        <Eye className="h-4 w-4 text-zinc-700" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenInNewTabForReceipt(rec)}
                        className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl transition-all cursor-pointer active:scale-95"
                        title="Abrir PDF"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleShareWhatsAppForReceipt(rec)}
                        className="p-2 bg-emerald-50 hover:bg-emerald-100 text-brand border border-emerald-200 rounded-xl transition-all cursor-pointer active:scale-95"
                        title="Compartilhar no WhatsApp"
                      >
                        <Share2 className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSelectForEdit(rec)}
                        className="px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer active:scale-95"
                        title="Editar / Reutilizar"
                      >
                        <span>Editar</span>
                      </button>

                      {onDeleteReceipt && (
                        <button
                          type="button"
                          onClick={() => handleDeleteReceiptAction(rec)}
                          className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all cursor-pointer active:scale-95"
                          title="Excluir Recibo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal de Pré-Visualização */}
      <ReceiptPdfPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        receipt={previewReceiptTarget || buildCurrentReceipt()}
        user={effectiveUserProfile}
        onOpenInNewTab={() => {
          if (previewReceiptTarget) handleOpenInNewTabForReceipt(previewReceiptTarget);
          else handleOpenInNewTab();
        }}
        onDownloadPDF={handleDownloadPDF}
        isGenerating={isGenerating}
        generationStep={generationStep}
      />
    </div>
  );
}
